import { z } from 'zod';
import { INCLUSION_STATUSES, VENUE_STATUSES } from '../constants.js';
import {
  auditFields,
  centsSchema,
  currencySchema,
  isoDateSchema,
  notesText,
  optionalText,
  shortText,
  ulidSchema,
  urlSchema,
} from '../common.js';
import { addressSchema } from './guest.js';

/**
 * One line of "does this place cover it, or do we pay someone else".
 *
 * The estimate is what makes the list worth filling in: a venue quoting less
 * and including nothing is more expensive than one quoting more and covering
 * the DJ and the photographer, and no amount of free-text chips shows that.
 */
export const venueInclusionSchema = z.object({
  /** A `VENUE_INCLUSION_KEYS` value, or `custom:<slug>` for something it has no word for. */
  key: z.string().trim().min(1).max(60),
  /** Only set on custom keys — catalog items are named by the translation file. */
  label: optionalText,
  status: z.enum(INCLUSION_STATUSES),
  /**
   * What you expect this to cost elsewhere. Your estimate, not a quote, and
   * only meaningful when the status is `extra`.
   */
  estimateCents: centsSchema.optional(),
  note: optionalText,
});
export type VenueInclusion = z.infer<typeof venueInclusionSchema>;

export const venueSchema = z.object({
  type: z.literal('Venue'),
  wedId: ulidSchema,
  venueId: ulidSchema,
  name: shortText,
  address: addressSchema.optional(),
  /** Set by the geocoder when an address resolves; used for the map pin. */
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  website: urlSchema.optional(),
  instagram: z.string().trim().max(40).optional(),
  contactName: optionalText,
  contactEmail: z.string().trim().email().max(254).optional(),
  contactPhone: z.string().trim().max(40).optional(),
  capacity: z.number().int().min(0).max(10000).optional(),
  quoteCents: centsSchema.optional(),
  currency: currencySchema,
  /**
   * Superseded by `inclusions`, and deliberately still here. These hold real
   * data in live accounts; `inclusionsFromLegacy` reads them when `inclusions`
   * is empty, and saving inclusions writes them back in step. They can go once
   * no venue has them.
   */
  includes: z.array(shortText).max(50).default([]),
  excludes: z.array(shortText).max(50).default([]),
  inclusions: z.array(venueInclusionSchema).max(60).default([]),
  tourDate: isoDateSchema.optional(),
  status: z.enum(VENUE_STATUSES).default('found'),
  photoKeys: z.array(z.string().max(512)).max(30).default([]),
  notes: notesText,
  ...auditFields,
});
export type Venue = z.infer<typeof venueSchema>;

export const createVenueSchema = venueSchema
  .omit({
    type: true,
    wedId: true,
    venueId: true,
    createdAt: true,
    updatedAt: true,
    updatedBy: true,
  })
  .partial()
  .required({ name: true });
export type CreateVenueInput = z.infer<typeof createVenueSchema>;
export const updateVenueSchema = createVenueSchema.partial();

/** One review per member per venue — the Goodreads-style part of the Venues tab. */
export const venueReviewSchema = z.object({
  type: z.literal('VenueReview'),
  wedId: ulidSchema,
  venueId: ulidSchema,
  userId: z.string(),
  reviewerName: optionalText,
  stars: z.number().int().min(1).max(5),
  liked: notesText,
  disliked: notesText,
  dealbreakers: notesText,
  wouldBook: z.boolean().optional(),
  photoKeys: z.array(z.string().max(512)).max(20).default([]),
  ...auditFields,
});
export type VenueReview = z.infer<typeof venueReviewSchema>;

export const upsertVenueReviewSchema = venueReviewSchema
  .omit({
    type: true,
    wedId: true,
    venueId: true,
    userId: true,
    reviewerName: true,
    createdAt: true,
    updatedAt: true,
    updatedBy: true,
  })
  .partial()
  .required({ stars: true });
export type UpsertVenueReviewInput = z.infer<typeof upsertVenueReviewSchema>;
